import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { API_KEY, LIVE_API_BASE_URL, getApiHeaders } from '../config/api';

const liveApiBase = `${LIVE_API_BASE_URL.replace(/\/$/, '')}/v1/live`;

type FrameAssetKind = 'mov' | 'webm' | 'webp';

type PresignResponse = {
  frameId: string;
  uploadUrl: string;
  fileKey: string;
  fileUrl: string;
};

/**
 * Client-side mirror of LIVE_FRAME_ASSET_RULES in tezda-live.
 *
 * These checks are a UX affordance only — the backend enforces the same extension, MIME and size
 * rules, because anything here is bypassed by calling the API directly. Keep the two in sync.
 */
const ASSET_RULES: Record<
  FrameAssetKind,
  { label: string; extension: string; accept: string; contentTypes: string[]; maxBytes: number; platform: string }
> = {
  mov: {
    label: 'iOS animation (.mov)',
    extension: '.mov',
    accept: '.mov,video/quicktime',
    // Browsers report .mov inconsistently; the extension is the real gate, same as on the server.
    contentTypes: ['video/quicktime', 'video/x-quicktime', 'application/octet-stream', ''],
    maxBytes: 25 * 1024 * 1024,
    platform: 'iOS',
  },
  webm: {
    label: 'Android animation (.webm)',
    extension: '.webm',
    accept: '.webm,video/webm',
    contentTypes: ['video/webm', 'application/octet-stream', ''],
    maxBytes: 25 * 1024 * 1024,
    platform: 'Android',
  },
  webp: {
    label: 'Picker thumbnail (.webp)',
    extension: '.webp',
    accept: '.webp,image/webp',
    contentTypes: ['image/webp'],
    maxBytes: 1 * 1024 * 1024,
    platform: 'Picker',
  },
};

const formatBytes = (bytes: number): string =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;

/** Returns an error string, or null when the file satisfies its slot's rules. */
function validateAsset(file: File, kind: FrameAssetKind): string | null {
  const rule = ASSET_RULES[kind];
  const name = file.name.toLowerCase();

  if (!name.endsWith(rule.extension)) {
    return `Must be a ${rule.extension} file — got "${file.name}".`;
  }
  // An empty type is accepted: some browsers report nothing for .mov/.webm.
  if (file.type && !rule.contentTypes.includes(file.type)) {
    return `Unexpected content type "${file.type}" for a ${rule.extension} file.`;
  }
  if (file.size > rule.maxBytes) {
    return `Too large — ${formatBytes(file.size)}, limit is ${formatBytes(rule.maxBytes)}.`;
  }
  if (file.size === 0) {
    return 'File is empty.';
  }
  return null;
}

type AssetState = {
  file: File | null;
  error: string;
  previewUrl: string;
  presign: PresignResponse | null;
};

const emptyAsset: AssetState = { file: null, error: '', previewUrl: '', presign: null };

const CreateLiveFrame: React.FC = () => {
  const navigate = useNavigate();
  const inputRefs = {
    mov: useRef<HTMLInputElement>(null),
    webm: useRef<HTMLInputElement>(null),
    webp: useRef<HTMLInputElement>(null),
  };

  const [name, setName] = useState('');
  const [assets, setAssets] = useState<Record<FrameAssetKind, AssetState>>({
    mov: { ...emptyAsset },
    webm: { ...emptyAsset },
    webp: { ...emptyAsset },
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploaded, setUploaded] = useState(false);

  // Object URLs are per-file and leak if not released when the selection changes or the page closes.
  useEffect(
    () => () => {
      Object.values(assets).forEach((asset) => {
        if (asset.previewUrl) URL.revokeObjectURL(asset.previewUrl);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const headerWithAuth = useCallback(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('No token found. Please log in.');
      return null;
    }
    if (!API_KEY) {
      toast.error('Missing API key. Set REACT_APP_API_KEY in your .env file.');
      return null;
    }
    return getApiHeaders(token);
  }, []);

  const handleFileChange = (kind: FrameAssetKind, file: File | null) => {
    setAssets((prev) => {
      if (prev[kind].previewUrl) URL.revokeObjectURL(prev[kind].previewUrl);

      if (!file) return { ...prev, [kind]: { ...emptyAsset } };

      const error = validateAsset(file, kind);
      return {
        ...prev,
        [kind]: {
          file,
          error: error || '',
          // Preview every asset, videos included — a frame is an animation, and a still thumbnail
          // says nothing about whether the alpha channel actually works.
          previewUrl: error ? '' : URL.createObjectURL(file),
          presign: null,
        },
      };
    });
    // Any change invalidates the previous upload round.
    setUploaded(false);
  };

  const kinds: FrameAssetKind[] = ['mov', 'webm', 'webp'];
  const allSelected = kinds.every((k) => assets[k].file !== null);
  const anyError = kinds.some((k) => assets[k].error !== '');
  const canUpload = name.trim().length > 0 && allSelected && !anyError && !uploading && !uploaded;

  const handleUpload = async () => {
    if (!name.trim()) {
      toast.error('Frame name is required.');
      return;
    }
    const missing = kinds.filter((k) => !assets[k].file);
    if (missing.length > 0) {
      toast.error(`Select all three assets — missing: ${missing.map((k) => ASSET_RULES[k].extension).join(', ')}`);
      return;
    }
    if (anyError) {
      toast.error('Fix the highlighted file errors first.');
      return;
    }

    const headers = headerWithAuth();
    if (!headers) return;

    setUploading(true);
    try {
      // Presign each asset separately, then PUT each one. Sequential rather than parallel: a 25 MB
      // .mov and a 25 MB .webm at once is enough to stall a slow connection, and an ordered failure
      // tells the admin exactly which asset broke.
      const results: Partial<Record<FrameAssetKind, PresignResponse>> = {};

      for (const kind of kinds) {
        const file = assets[kind].file as File;

        const presignRes = await fetch(`${liveApiBase}/frames/presign`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ name: name.trim(), kind }),
        });
        const presignJson = await presignRes.json();
        if (!presignRes.ok) {
          throw new Error(presignJson.message || `Failed to get upload URL for ${ASSET_RULES[kind].extension}`);
        }
        const presign = presignJson.data as PresignResponse;

        const uploadRes = await fetch(presign.uploadUrl, {
          method: 'PUT',
          body: await file.arrayBuffer(),
          headers: { 'Content-Type': ASSET_RULES[kind].contentTypes[0] || 'application/octet-stream' },
        });
        if (!uploadRes.ok) throw new Error(`Failed to upload ${ASSET_RULES[kind].extension}`);

        results[kind] = presign;
      }

      setAssets((prev) => ({
        mov: { ...prev.mov, presign: results.mov || null },
        webm: { ...prev.webm, presign: results.webm || null },
        webp: { ...prev.webp, presign: results.webp || null },
      }));
      setUploaded(true);
      toast.success('All three assets uploaded — save the frame to complete.');
    } catch (error) {
      toast.error((error as Error).message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!uploaded) {
      toast.error('Upload the assets first.');
      return;
    }
    const headers = headerWithAuth();
    if (!headers) return;

    const movKey = assets.mov.presign?.fileKey;
    const webmKey = assets.webm.presign?.fileKey;
    const thumbnailKey = assets.webp.presign?.fileKey;
    if (!movKey || !webmKey || !thumbnailKey) {
      toast.error('Upload result incomplete — re-upload the assets.');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${liveApiBase}/frames`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          // The .mov presign's frameId is the record id; all three assets belong to one frame.
          frameId: assets.mov.presign?.frameId,
          name: name.trim(),
          movKey,
          webmKey,
          thumbnailKey,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to save frame');
      toast.success(json.message || 'Frame saved');
      navigate('/live-frames');
    } catch (error) {
      toast.error((error as Error).message || 'Failed to save frame');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
      <Navbar />

      <div className='max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6'>
        <div className='flex items-center justify-between'>
          <div>
            <h1 className='text-2xl sm:text-3xl font-bold text-white'>Upload Live Frame</h1>
            <p className='text-sm text-gray-400'>
              A transparent animated border drawn over the video. All three assets are required.
            </p>
          </div>
          <button
            onClick={() => navigate('/live-frames')}
            className='px-4 py-2 text-sm font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition'
          >
            Back to Frames
          </button>
        </div>

        <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
          {/* Step 1 — Upload */}
          <div className='lg:col-span-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 space-y-5'>
            <div>
              <p className='text-xs uppercase tracking-[0.2em] text-gray-500 mb-2'>Step 1</p>
              <h2 className='text-lg font-semibold text-white'>Upload Assets</h2>
              <p className='text-xs text-gray-400 mt-1'>
                iOS plays the .mov and Android the .webm — a frame missing either is unrenderable on half the fleet.
              </p>
            </div>

            <div>
              <label className='block text-xs text-gray-400 mb-1'>Frame Name *</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FF9C15]/60'
                placeholder='e.g. Gold Celebration'
              />
            </div>

            {kinds.map((kind) => {
              const rule = ASSET_RULES[kind];
              const asset = assets[kind];
              return (
                <div key={kind} className='border-t border-white/10 pt-4'>
                  <div className='flex items-center justify-between mb-1'>
                    <label className='block text-xs text-gray-400'>{rule.label} *</label>
                    <span className='text-[10px] uppercase tracking-wider text-gray-500'>
                      max {formatBytes(rule.maxBytes)}
                    </span>
                  </div>
                  <input
                    ref={inputRefs[kind]}
                    type='file'
                    accept={rule.accept}
                    onChange={(e) => handleFileChange(kind, e.target.files?.[0] || null)}
                    className='w-full text-sm text-gray-300'
                  />

                  {asset.file && !asset.error && (
                    <div className='mt-1 text-xs text-gray-400'>
                      {asset.file.name} ({formatBytes(asset.file.size)})
                    </div>
                  )}

                  {asset.error && (
                    <div className='mt-2 flex items-start gap-2 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2'>
                      <span className='text-red-400 text-xs font-semibold'>Invalid</span>
                      <span className='text-red-200 text-xs'>{asset.error}</span>
                    </div>
                  )}

                  {asset.previewUrl && kind === 'webp' && (
                    <img
                      src={asset.previewUrl}
                      alt='Thumbnail preview'
                      className='mt-2 h-24 w-24 object-cover rounded-lg border border-white/10 bg-[conic-gradient(#334155_0_25%,#1e293b_0_50%,#334155_0_75%,#1e293b_0)] bg-[length:16px_16px]'
                    />
                  )}

                  {asset.previewUrl && kind !== 'webp' && (
                    <video
                      src={asset.previewUrl}
                      muted
                      loop
                      autoPlay
                      playsInline
                      // Checkerboard behind the video so a missing/opaque alpha channel is visible
                      // here rather than on a live stream.
                      className='mt-2 h-32 rounded-lg border border-white/10 bg-[conic-gradient(#334155_0_25%,#1e293b_0_50%,#334155_0_75%,#1e293b_0)] bg-[length:16px_16px]'
                    />
                  )}
                </div>
              );
            })}

            <button
              onClick={handleUpload}
              disabled={!canUpload}
              className='w-full py-2.5 bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all disabled:opacity-60'
            >
              {uploading ? 'Uploading...' : uploaded ? 'Uploaded' : 'Upload All Three'}
            </button>

            {!allSelected && (
              <p className='text-xs text-gray-500 text-center'>
                Select the .mov, .webm and .webp to continue.
              </p>
            )}
          </div>

          {/* Step 2 — Save */}
          <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 space-y-4'>
            <div>
              <p className='text-xs uppercase tracking-[0.2em] text-gray-500 mb-2'>Step 2</p>
              <h2 className='text-lg font-semibold text-white'>Save Frame</h2>
              <p className='text-xs text-gray-400 mt-1'>Adds the frame to the catalog, active by default.</p>
            </div>

            <div className='bg-black/20 border border-white/10 rounded-lg p-4 space-y-2 text-xs text-gray-400'>
              <div className='flex justify-between'>
                <span>Name</span>
                <span className='text-white'>{name || '—'}</span>
              </div>
              <div className='flex justify-between'>
                <span>Frame ID</span>
                <span className='text-white font-mono text-[10px]'>{assets.mov.presign?.frameId || '—'}</span>
              </div>
              {kinds.map((kind) => (
                <div key={kind} className='flex justify-between'>
                  <span>{ASSET_RULES[kind].platform}</span>
                  <span className={assets[kind].presign ? 'text-emerald-300' : 'text-gray-500'}>
                    {assets[kind].presign ? 'Uploaded' : assets[kind].file ? 'Pending' : 'None'}
                  </span>
                </div>
              ))}
            </div>

            <button
              onClick={handleSave}
              disabled={saving || !uploaded}
              className='w-full py-2.5 bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all disabled:opacity-60'
            >
              {saving ? 'Saving...' : 'Save Frame'}
            </button>

            {!uploaded && <p className='text-xs text-gray-500 text-center'>Complete Step 1 first.</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateLiveFrame;
